/**
 * Centralized entitlements cache to deduplicate repeated queries to users_public table.
 * 
 * Features:
 * - In-memory cache keyed by userId with TTL (default 60s)
 * - In-flight request deduplication (concurrent calls return same Promise)
 * - Explicit invalidate() after promo redemption or subscription changes
 * - Dev-only logging to track cache hits vs network fetches
 * 
 * Usage:
 *   import { getUserEntitlements, invalidateEntitlements } from '@/lib/entitlementsCache';
 *   const data = await getUserEntitlements(supabase, userId);
 *   invalidateEntitlements(userId); // after promo/subscription changes
 */

const isDev = import.meta.env.MODE === "development";

// In-memory cache: Map<userId, {data, fetchedAt}>
const entitlementsCache = new Map();

// In-flight request tracking: Map<userId, Promise>
const inFlightRequests = new Map();

// Cache TTL in milliseconds (default 60s, configurable)
const CACHE_TTL_MS = 60_000;

/**
 * Get user entitlements from cache or fetch from users_public table.
 * Deduplicates concurrent requests and caches results for CACHE_TTL_MS.
 * 
 * @param {Object} supabase - Supabase client instance
 * @param {string} userId - User ID to fetch entitlements for
 * @returns {Promise<{trial_started_at: string|null, trial_ends_at: string|null, access_until: string|null}>}
 */
export async function getUserEntitlements(supabase, userId) {
  if (!userId) {
    if (isDev) console.log("[EntitlementsCache] No userId provided, returning null");
    return null;
  }

  const now = Date.now();
  
  // Check cache first
  const cached = entitlementsCache.get(userId);
  if (cached) {
    const age = now - cached.fetchedAt;
    if (age < CACHE_TTL_MS) {
      if (isDev) {
        console.log(`[EntitlementsCache] ✓ Cache HIT for userId=${userId.slice(0, 8)}... (age=${Math.round(age / 1000)}s)`);
      }
      return cached.data;
    } else {
      if (isDev) {
        console.log(`[EntitlementsCache] Cache EXPIRED for userId=${userId.slice(0, 8)}... (age=${Math.round(age / 1000)}s, TTL=${CACHE_TTL_MS / 1000}s)`);
      }
      entitlementsCache.delete(userId);
    }
  }

  // Check if a fetch is already in-flight for this user
  const inFlight = inFlightRequests.get(userId);
  if (inFlight) {
    if (isDev) {
      console.log(`[EntitlementsCache] ⏳ IN-FLIGHT dedupe for userId=${userId.slice(0, 8)}...`);
    }
    return inFlight;
  }

  // Perform network fetch
  if (isDev) {
    console.log(`[EntitlementsCache] 🌐 Network FETCH for userId=${userId.slice(0, 8)}...`);
  }

  const fetchPromise = (async () => {
    try {
      const [userPublicResult, subscriptionResult] = await Promise.all([
        supabase
          .from("users_public")
          .select("trial_started_at, trial_ends_at, access_until")
          .eq("id", userId)
          .maybeSingle(),
        supabase
          .from("subscriptions")
          .select("user_id, status, current_period_end, stripe_subscription_id, stripe_customer_id, price_id, cancel_at_period_end")
          .eq("user_id", userId)
          .maybeSingle(),
      ]);

      const { data: userPublic, error: userErr } = userPublicResult || {};
      const { data: subscription, error: subscriptionErr } = subscriptionResult || {};

      if (userErr) {
        console.error("[EntitlementsCache] users_public query error:", userErr);
        throw userErr;
      }

      // Subscription row is optional for free users - don't throw if missing
      if (subscriptionErr && subscriptionErr.code !== 'PGRST116') {
        console.error("[EntitlementsCache] subscriptions query error:", subscriptionErr);
        throw subscriptionErr;
      }

      const payload = {
        userPublic: userPublic || null,
        subscription: subscription || null,
      };

      // Cache the result
      entitlementsCache.set(userId, {
        data: payload,
        fetchedAt: Date.now(),
      });

      if (isDev) {
        console.log(`[EntitlementsCache] ✓ Cached result for userId=${userId.slice(0, 8)}...`, {
          trial_started_at: userPublic?.trial_started_at,
          trial_ends_at: userPublic?.trial_ends_at,
          access_until: userPublic?.access_until,
          hasSubscription: !!subscription,
          stripe_subscription_id: subscription?.stripe_subscription_id,
        });
      }

      return payload;
    } finally {
      // Always clean up in-flight tracking
      inFlightRequests.delete(userId);
    }
  })();

  // Track in-flight request
  inFlightRequests.set(userId, fetchPromise);

  return fetchPromise;
}

/**
 * Invalidate cached entitlements for a specific user or all users.
 * Call this after promo redemption, subscription changes, or trial starts.
 * 
 * @param {string|null} userId - User ID to invalidate, or null to clear all cache
 */
export function invalidateEntitlements(userId = null) {
  if (userId) {
    if (isDev) {
      console.log(`[EntitlementsCache] 🗑️ Invalidating cache for userId=${userId.slice(0, 8)}...`);
    }
    entitlementsCache.delete(userId);
    inFlightRequests.delete(userId);
  } else {
    if (isDev) {
      console.log(`[EntitlementsCache] 🗑️ Clearing ALL entitlements cache (${entitlementsCache.size} entries)`);
    }
    entitlementsCache.clear();
    inFlightRequests.clear();
  }
}

/**
 * Get cache statistics for debugging.
 * 
 * @returns {{cacheSize: number, inFlightCount: number, entries: Array}}
 */
export function getEntitlementsCacheStats() {
  const now = Date.now();
  const entries = Array.from(entitlementsCache.entries()).map(([userId, cached]) => ({
    userId: userId.slice(0, 8) + "...",
    age: Math.round((now - cached.fetchedAt) / 1000),
    expired: now - cached.fetchedAt >= CACHE_TTL_MS,
    data: cached.data,
  }));

  return {
    cacheSize: entitlementsCache.size,
    inFlightCount: inFlightRequests.size,
    entries,
  };
}

// Expose cache stats to window in dev mode for debugging
if (isDev && typeof window !== "undefined") {
  window.__entitlementsCache = {
    getStats: getEntitlementsCacheStats,
    invalidate: invalidateEntitlements,
  };
}
